import React, { useEffect, useState } from 'react';
import { Users, CheckCircle, XCircle, ShoppingCart, Download } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { AnalyticsAPI, MobileUsersAnalytics } from '../../lib/api';

export default function UsersAnalyticsPage() {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState<MobileUsersAnalytics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<'day' | 'week' | 'month' | 'year'>('month');

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await AnalyticsAPI.getMobileUsersAnalytics({ period });
      if (response.success) {
        setAnalytics(response.data);
      }
    } catch (err: any) {
      console.error('Error fetching users analytics:', err);
      setError(err.message || 'Failed to load users analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading users analytics...</p>
        </div>
      </div>
    );
  }

  if (error || !analytics) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Failed to load analytics'}</p>
          <Button onClick={fetchAnalytics}>Retry</Button>
        </div>
      </div>
    );
  }

  const { data } = analytics;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold">Users Analytics</h1>
          <p className="text-muted-foreground">Mobile user insights and engagement metrics</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <select 
            className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={period}
            onChange={(e) => setPeriod(e.target.value as any)}
          >
            <option value="day">Last 24 hours</option>
            <option value="week">Last 7 days</option>
            <option value="month">Last 30 days</option>
            <option value="year">Last year</option>
          </select>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-muted-foreground">Total Users</p>
              <Users className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-2xl font-bold">{data.totalUsers}</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-muted-foreground">Verified Users</p>
              <CheckCircle className="h-4 w-4 text-green-600" />
            </div>
            <p className="text-2xl font-bold">{data.verifiedUsers}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {data.totalUsers > 0 ? ((data.verifiedUsers / data.totalUsers) * 100).toFixed(1) : 0}%
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-muted-foreground">Users with Orders</p>
              <ShoppingCart className="h-4 w-4 text-blue-600" />
            </div>
            <p className="text-2xl font-bold">{data.usersWithOrders}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {data.totalUsers > 0 ? ((data.usersWithOrders / data.totalUsers) * 100).toFixed(1) : 0}%
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-muted-foreground">Unverified Users</p>
              <XCircle className="h-4 w-4 text-red-600" />
            </div>
            <p className="text-2xl font-bold">{data.unverifiedUsers}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {data.totalUsers > 0 ? ((data.unverifiedUsers / data.totalUsers) * 100).toFixed(1) : 0}%
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Visual representations */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Verification Status</CardTitle>
            <CardDescription>User verification breakdown</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm font-medium">Verified Users</span>
                  <span className="text-sm text-muted-foreground">
                    {data.verifiedUsers} ({data.totalUsers > 0 ? ((data.verifiedUsers / data.totalUsers) * 100).toFixed(1) : 0}%)
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-3">
                  <div
                    className="bg-green-500 rounded-full h-3"
                    style={{ width: `${data.totalUsers > 0 ? (data.verifiedUsers / data.totalUsers) * 100 : 0}%` }}
                  />
                </div>
              </div>

              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm font-medium">Unverified Users</span>
                  <span className="text-sm text-muted-foreground">
                    {data.unverifiedUsers} ({data.totalUsers > 0 ? ((data.unverifiedUsers / data.totalUsers) * 100).toFixed(1) : 0}%)
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-3">
                  <div
                    className="bg-red-500 rounded-full h-3"
                    style={{ width: `${data.totalUsers > 0 ? (data.unverifiedUsers / data.totalUsers) * 100 : 0}%` }}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>User Engagement</CardTitle>
            <CardDescription>Order activity breakdown</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm font-medium">Users with Orders</span>
                  <span className="text-sm text-muted-foreground">
                    {data.usersWithOrders} ({data.totalUsers > 0 ? ((data.usersWithOrders / data.totalUsers) * 100).toFixed(1) : 0}%)
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-3">
                  <div
                    className="bg-blue-500 rounded-full h-3"
                    style={{ width: `${data.totalUsers > 0 ? (data.usersWithOrders / data.totalUsers) * 100 : 0}%` }}
                  />
                </div>
              </div>

              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm font-medium">Users without Orders</span>
                  <span className="text-sm text-muted-foreground">
                    {data.totalUsers - data.usersWithOrders} ({data.totalUsers > 0 ? (((data.totalUsers - data.usersWithOrders) / data.totalUsers) * 100).toFixed(1) : 0}%)
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-3">
                  <div
                    className="bg-gray-400 rounded-full h-3"
                    style={{ width: `${data.totalUsers > 0 ? ((data.totalUsers - data.usersWithOrders) / data.totalUsers) * 100 : 0}%` }}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Summary grid */}
      <Card>
        <CardHeader>
          <CardTitle>User Overview</CardTitle>
          <CardDescription>Comprehensive user statistics</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="rounded-lg border p-4">
              <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center mb-2">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div className="text-2xl font-bold">{data.totalUsers}</div>
              <div className="text-xs text-muted-foreground">Total Users</div>
            </div>

            <div className="rounded-lg border p-4">
              <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center mb-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div className="text-2xl font-bold">{data.verifiedUsers}</div>
              <div className="text-xs text-muted-foreground">Verified Users</div>
            </div>

            <div className="rounded-lg border p-4">
              <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center mb-2">
                <ShoppingCart className="h-5 w-5 text-indigo-600" />
              </div>
              <div className="text-2xl font-bold">{data.usersWithOrders}</div>
              <div className="text-xs text-muted-foreground">With Orders</div>
            </div>

            <div className="rounded-lg border p-4">
              <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center mb-2">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
              <div className="text-2xl font-bold">{data.unverifiedUsers}</div>
              <div className="text-xs text-muted-foreground">Unverified</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
